import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Send, Loader2, Twitch, Youtube, Instagram } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = () => {
  const { lang, t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(t.contact.ok);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t.contact.ko);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-transparent px-5 py-4 text-sm text-white placeholder:text-muted2/60 outline-none transition-colors duration-300 focus:border-neon";

  return (
    <section id="contact" className="border-t border-white/10 px-6 py-28 md:px-12 md:py-40" data-testid="contact-section">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 md:grid-cols-12">
        <Reveal className="md:col-span-5">
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon">{t.contact.kicker}</p>
          <h2 className="font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl lg:text-6xl">
            {t.contact.title}
          </h2>
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-muted2 md:text-base">{t.contact.sub}</p>
          <a
            href="mailto:info@fabiocomelli.it"
            data-testid="contact-email-link"
            className="mt-8 inline-block font-mono text-sm text-neon underline-offset-4 transition-colors duration-300 hover:underline"
          >
            info@fabiocomelli.it
          </a>
          <a
            href="https://www.twitch.tv/DottorFabio"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-twitch-link"
            className="mt-4 flex items-center gap-2 font-mono text-sm text-muted2 transition-colors duration-300 hover:text-neon"
          >
            <Twitch className="h-4 w-4" />
            twitch.tv/DottorFabio
          </a>
          <a
            href="https://www.youtube.com/@fabiocomelli"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-youtube-link"
            className="mt-3 flex items-center gap-2 font-mono text-sm text-muted2 transition-colors duration-300 hover:text-neon"
          >
            <Youtube className="h-4 w-4" />
            youtube.com/@fabiocomelli
          </a>
          <a
            href="https://www.instagram.com/fabio_comelli/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-instagram-link"
            className="mt-3 flex items-center gap-2 font-mono text-sm text-muted2 transition-colors duration-300 hover:text-neon"
          >
            <Instagram className="h-4 w-4" />
            instagram.com/fabio_comelli
          </a>
        </Reveal>
        <Reveal delay={0.15} className="md:col-span-7">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input required value={form.name} onChange={set("name")} placeholder={t.contact.name} className={inputCls} data-testid="contact-name-input" />
              <input required type="email" value={form.email} onChange={set("email")} placeholder={t.contact.email} className={inputCls} data-testid="contact-email-input" />
            </div>
            <textarea required rows={6} value={form.message} onChange={set("message")} placeholder={t.contact.message} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="group inline-flex items-center gap-3 rounded-full bg-neon px-8 py-4 font-mono text-xs font-bold uppercase tracking-widest text-black transition-transform duration-300 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              {sending ? t.contact.sending : t.contact.send}
            </button>
            <p className="text-xs leading-relaxed text-muted2" data-testid="contact-privacy-note">
              {t.contact.privacyNotePre}
              <Link to="/privacy/sito" data-testid="contact-privacy-link" className="text-neon underline underline-offset-4 transition-colors duration-300 hover:opacity-80">
                {t.contact.privacyNoteLink}
              </Link>
              {t.contact.privacyNotePost}
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
